import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RequestCategory } from '@kfolks/types'
import { Badge } from '@/shared/components/atoms/ui/badge'
import { Button } from '@/shared/components/atoms/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/atoms/ui/dropdown-menu'
import { Input } from '@/shared/components/atoms/ui/input'
import { Modal } from '@/components/Modal'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/atoms/ui/table'
import { type RequestTypeDTO, requestCenterApi } from '../api/request-center.api'
import { requestCenterKeys } from '../hooks/query-keys'
import { useCrudState } from '../hooks/useCrudState'
import { Icon } from '@/shared/components/atoms/Icon'

interface TypeForm {
	slug: string
	name: string
	category: RequestCategory
	description: string
}

const blankForm = (): TypeForm => ({
	slug: '',
	name: '',
	category: RequestCategory.EMPLOYEES_LIFECYCLE,
	description: '',
})

export function RequestTypesPage() {
	const crud = useCrudState<RequestTypeDTO, TypeForm>()
	const { list, save, del } = useRequestTypesMutations(crud)
	return (
		<div className="space-y-4">
			<header className="flex items-center justify-between">
				<h1 className="text-2xl font-semibold tracking-tight">Request Types</h1>
				<Button onClick={() => crud.openCreate(blankForm())}>New Request Type</Button>
			</header>
			<RequestTypesTable
				list={list}
				deletePending={del.isPending}
				onEdit={(t) =>
					crud.openEdit(t, { slug: t.slug, name: t.name, category: t.category, description: t.description ?? '' })
				}
				onDelete={(id) => del.mutate(id)}
			/>
			<TypeFormModal
				open={crud.isOpen}
				editing={crud.isEditing}
				form={crud.form}
				saving={save.isPending}
				saveError={save.error}
				onChange={crud.setForm}
				onClose={crud.close}
				onSubmit={(input) => save.mutate(input)}
			/>
		</div>
	)
}

function useRequestTypesMutations(crud: ReturnType<typeof useCrudState<RequestTypeDTO, TypeForm>>) {
	const queryClient = useQueryClient()
	const list = useQuery({
		queryKey: requestCenterKeys.types.admin(),
		queryFn: () => requestCenterApi.listRequestTypes({ includeInactive: true, limit: 100 }),
	})
	const save = useMutation({
		mutationFn: async (input: TypeForm) =>
			crud.editing
				? requestCenterApi.updateRequestType(crud.editing.id, {
						slug: input.slug,
						name: input.name,
						category: input.category,
						description: input.description || null,
					})
				: requestCenterApi.createRequestType({
						slug: input.slug,
						name: input.name,
						category: input.category,
						description: input.description || null,
						formSchema: { version: '1.0', fields: [] },
					}),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: requestCenterKeys.types.all })
			crud.close()
		},
	})
	const del = useMutation({
		mutationFn: (id: string) => requestCenterApi.deleteRequestType(id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: requestCenterKeys.types.all }),
	})
	return { list, save, del }
}

function RequestTypesTable({
	list,
	deletePending,
	onEdit,
	onDelete,
}: {
	list: { data: RequestTypeDTO[] | undefined; isLoading: boolean; error: unknown }
	deletePending: boolean
	onEdit: (t: RequestTypeDTO) => void
	onDelete: (id: string) => void
}) {
	if (list.isLoading) return <TableSkeleton />
	if (list.error) return <ErrorState message="Failed to load request types" />
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Slug</TableHead>
					<TableHead>Name</TableHead>
					<TableHead>Category</TableHead>
					<TableHead>Status</TableHead>
					<TableHead className="text-right">Actions</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{(list.data ?? []).map((t) => (
					<RequestTypeRow key={t.id} type={t} deletePending={deletePending} onEdit={onEdit} onDelete={onDelete} />
				))}
			</TableBody>
		</Table>
	)
}

function RequestTypeRow({
	type,
	deletePending,
	onEdit,
	onDelete,
}: {
	type: RequestTypeDTO
	deletePending: boolean
	onEdit: (t: RequestTypeDTO) => void
	onDelete: (id: string) => void
}) {
	return (
		<TableRow>
			<TableCell className="font-mono text-xs">{type.slug}</TableCell>
			<TableCell>{type.name}</TableCell>
			<TableCell className="text-xs text-muted-foreground">{type.category}</TableCell>
			<TableCell>
				<Badge variant={type.isActive ? 'default' : 'outline'}>{type.isActive ? 'active' : 'inactive'}</Badge>
			</TableCell>
			<TableCell className="whitespace-nowrap text-right">
				<div className="flex flex-nowrap items-center justify-end gap-2">
					<Button variant="outline" size="sm" asChild>
						<Link to={`/admin/request-center/types/${type.id}/form`}>Form</Link>
					</Button>
					<Button variant="outline" size="sm" asChild>
						<Link to={`/admin/request-center/types/${type.id}/workflow`}>Workflow</Link>
					</Button>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" size="sm" aria-label="More actions">
								<Icon name="Ellipsis" size="sm" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onSelect={() => onEdit(type)}>Edit</DropdownMenuItem>
							<DropdownMenuItem
								disabled={deletePending}
								onSelect={() => {
									if (window.confirm(`Soft-delete ${type.slug}?`)) onDelete(type.id)
								}}
								className="text-red-600 focus:text-red-700"
							>
								Delete
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</TableCell>
		</TableRow>
	)
}

function TypeFormModal({
	open,
	editing,
	form,
	saving,
	saveError,
	onChange,
	onClose,
	onSubmit,
}: {
	open: boolean
	editing: boolean
	form: TypeForm | null
	saving: boolean
	saveError: unknown
	onChange: (next: TypeForm) => void
	onClose: () => void
	onSubmit: (input: TypeForm) => void
}) {
	if (!form) return null
	return (
		<Modal open={open} onClose={onClose} title={editing ? 'Edit request type' : 'New request type'} maxWidth="lg">
			<form
				onSubmit={(e) => {
					e.preventDefault()
					onSubmit(form)
				}}
				className="space-y-3"
			>
				<TypeFormFields form={form} onChange={onChange} />
				{saveError ? (
					<p role="alert" className="text-sm text-destructive">
						{messageFor(saveError)}
					</p>
				) : null}
				<TypeFormActions saving={saving} onClose={onClose} />
			</form>
		</Modal>
	)
}

function TypeFormFields({ form, onChange }: { form: TypeForm; onChange: (next: TypeForm) => void }) {
	return (
		<>
			<TextRow label="Slug" value={form.slug} onChange={(v) => onChange({ ...form, slug: v })} required />
			<TextRow label="Name" value={form.name} onChange={(v) => onChange({ ...form, name: v })} required />
			<label className="block space-y-1">
				<span className="text-sm font-medium">
					Category <span className="ml-0.5 text-destructive">*</span>
				</span>
				<select
					className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
					value={form.category}
					onChange={(e) => onChange({ ...form, category: e.target.value as RequestCategory })}
				>
					{Object.values(RequestCategory).map((c) => (
						<option key={c} value={c}>
							{c}
						</option>
					))}
				</select>
			</label>
			<label className="block space-y-1">
				<span className="text-sm font-medium">Description</span>
				<textarea
					className="flex min-h-[5rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
					value={form.description}
					onChange={(e) => onChange({ ...form, description: e.target.value })}
				/>
			</label>
		</>
	)
}

function TypeFormActions({ saving, onClose }: { saving: boolean; onClose: () => void }) {
	return (
		<div className="flex justify-end gap-2 pt-2">
			<Button type="button" variant="outline" onClick={onClose}>
				Cancel
			</Button>
			<Button type="submit" disabled={saving}>
				{saving ? 'Saving…' : 'Save'}
			</Button>
		</div>
	)
}

function TextRow({
	label,
	value,
	onChange,
	required,
}: {
	label: string
	value: string
	onChange: (next: string) => void
	required?: boolean
}) {
	return (
		<label className="block space-y-1">
			<span className="text-sm font-medium">
				{label}
				{required ? <span className="ml-0.5 text-destructive">*</span> : null}
			</span>
			<Input value={value} onChange={(e) => onChange(e.target.value)} required={required} />
		</label>
	)
}

function TableSkeleton() {
	return (
		<div className="space-y-2">
			{[0, 1, 2].map((i) => (
				<div key={i} className="h-10 animate-pulse rounded bg-muted/40" />
			))}
		</div>
	)
}

function ErrorState({ message }: { message: string }) {
	return (
		<div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
			{message}
		</div>
	)
}

function messageFor(err: unknown): string {
	return err instanceof Error ? err.message : 'Save failed'
}
